'use client';

import { IT_INT } from '@slims/shared/constants';
import { apiFetch } from '@/lib/utils/api';
import { Stack } from '@slims/ui/atoms';
import { getInitials } from '@slims/ui/lib';
import { CardBadge, PanelCard, PanelCardTitle } from '@slims/ui/molecules';
import { ProfileIdentityCard, ProfileInfoRow, SignOutCard } from '@slims/ui/organisms';
import { PageContainer, PageHeader } from '@slims/ui/templates';
import { Zap } from 'lucide-react';
import { useRouter } from '@/lib/next/navigation';
import { useState } from 'react';
import { toast } from 'sonner';
import { ScopeSelector } from '@/components/layout/scope-selector';
import { NotificationPreferencesCard } from '@/components/profile/cer-preferences';
import { createSupabaseBrowser } from '@/lib/supabase/client';
import { useContextOrg } from '@/hooks/use-context-org';
import { hasRoleAtLeast } from '@slims/shared/auth';
import { useAuthStore } from '@/stores/auth-store';
import { useCERStore } from '@/stores/cer-store';

const ROLE_LABELS: Record<string, string> = {
	superadmin: 'Super Admin',
	admin: 'Amministratore',
	referent: 'Referente',
	member: 'Socio',
};

const ZONE_LABELS: Record<string, string> = {
	nord: 'Nord Italia',
	centro: 'Centro Italia',
	sud: 'Sud Italia',
};

function formatDate(dateStr: string | null): string {
	if (!dateStr) return '-';
	return new Date(dateStr).toLocaleDateString('it-IT', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	});
}

export default function ProfiloPage() {
	const router = useRouter();
	const { email, role, fullName, avatarUrl, createdAt, setFullName, setAvatarUrl } =
		useAuthStore();
	const contextOrg = useContextOrg();
	const config = useCERStore((s) => s.config);

	const [editing, setEditing] = useState(false);
	const [draftName, setDraftName] = useState('');
	const [saving, setSaving] = useState(false);
	const [uploadingAvatar, setUploadingAvatar] = useState(false);

	const displayName = fullName ?? email ?? '-';
	const roleLabel = ROLE_LABELS[role ?? ''] ?? role ?? '-';
	// Le preferenze della configurazione sono per-organizzazione: le vede chi la
	// amministra (INB-107).
	const isAdmin = !!role && hasRoleAtLeast(role, 'admin');
	const cerName = contextOrg?.cerNome ?? config.nome ?? '-';
	const membersCount = config.membersCount ?? config.soci;
	const initials = getInitials(editing ? draftName || null : fullName, email);

	const handleSignOut = async () => {
		const supabase = createSupabaseBrowser();
		await supabase.auth.signOut();
		router.push('/sign-in');
	};

	const startEditing = () => {
		setDraftName(fullName ?? '');
		setEditing(true);
	};

	const cancelEditing = () => {
		setEditing(false);
		setDraftName('');
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			const res = await apiFetch('/api/auth/me', {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ fullName: draftName }),
			});
			if (!res.ok) throw new Error('Errore nel salvataggio');
			setFullName(draftName.trim() || null);
			setEditing(false);
			toast.success('Profilo aggiornato');
		} catch {
			toast.error('Errore nel salvataggio del profilo');
		} finally {
			setSaving(false);
		}
	};

	const handleAvatarUpload = async (file: File) => {
		if (file.size > 5 * 1024 * 1024) {
			toast.error('Immagine troppo grande (max 5 MB)');
			return;
		}
		setUploadingAvatar(true);
		try {
			const fd = new FormData();
			fd.append('file', file);
			const res = await apiFetch('/api/auth/avatar', { method: 'POST', body: fd });
			if (!res.ok) {
				const err = (await res.json().catch(() => null)) as { error?: string } | null;
				throw new Error(err?.error ?? 'Upload fallito');
			}
			const { url } = (await res.json()) as { url: string };
			setAvatarUrl(url);
			toast.success('Immagine profilo aggiornata');
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Errore upload');
		} finally {
			setUploadingAvatar(false);
		}
	};

	const handleAvatarDelete = async () => {
		setUploadingAvatar(true);
		try {
			await apiFetch('/api/auth/avatar', { method: 'DELETE' });
			setAvatarUrl(null);
			toast.success('Immagine profilo rimossa');
		} catch {
			toast.error('Errore nella rimozione');
		} finally {
			setUploadingAvatar(false);
		}
	};

	return (
		<PageContainer width="narrow">
			<PageHeader
				size="display"
				title="Il mio profilo"
				description="Gestisci le tue informazioni personali e le impostazioni account."
			/>

			<Stack gap="lg">
				<ProfileIdentityCard
					editing={editing}
					saving={saving}
					uploadingAvatar={uploadingAvatar}
					displayName={displayName}
					draftName={draftName}
					onDraftNameChange={setDraftName}
					email={email}
					avatarUrl={avatarUrl}
					initials={initials}
					onStartEdit={startEditing}
					onCancelEdit={cancelEditing}
					onSave={handleSave}
					onUploadAvatar={handleAvatarUpload}
					onDeleteAvatar={handleAvatarDelete}
				/>

				<PanelCard title="Account" aside={<CardBadge tone="muted">{roleLabel}</CardBadge>}>
					<div>
						<ProfileInfoRow label="Ruolo" value={roleLabel} mono />
						<ProfileInfoRow label="Iscritto dal" value={formatDate(createdAt)} />
						<ProfileInfoRow label="Email verificata" value={email ? 'Sì' : '-'} />
					</div>
				</PanelCard>

				<PanelCard
					title={
						<PanelCardTitle icon={Zap} tone="accent">
							Comunità Energetica
						</PanelCardTitle>
					}
					aside={<CardBadge tone="accent">{cerName}</CardBadge>}
				>
					<div>
						{/* Scelta della configurazione attiva, come nel profilo mobile (INB-107). */}
						<ScopeSelector />
						<ProfileInfoRow label="CER" value={cerName} mono />
						{config.codiceGSE && <ProfileInfoRow label="Codice GSE" value={config.codiceGSE} mono />}
						{config.comune && <ProfileInfoRow label="Comune" value={config.comune} />}
						{config.kwp > 0 && (
							<ProfileInfoRow
								label="Potenza impianto"
								value={`${config.kwp.toLocaleString('it-IT', IT_INT)} kWp`}
							/>
						)}
						{config.zonaGeografica && (
							<ProfileInfoRow
								label="Zona geografica"
								value={ZONE_LABELS[config.zonaGeografica] ?? config.zonaGeografica}
							/>
						)}
						<ProfileInfoRow label="Soci nella CER" value={String(membersCount)} />
					</div>
				</PanelCard>

				{isAdmin && <NotificationPreferencesCard />}

				<SignOutCard onSignOut={handleSignOut} />
			</Stack>
		</PageContainer>
	);
}
